// Per-metric trend card. Renders one chart.js Line with:
//   - the time-series from /v1/employees/{id}/measurements (single metric)
//   - optional personal baseline band (mean ± 1 SD), drawn as a translucent
//     fill between two static datasets
//   - the SDK absolute range as faint annotation lines on the y-axis
//
// SPEC-009 §4.5 trend-chart row.

import { useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import type { ChartDataset, ChartOptions } from "chart.js";
import { useI18n } from "@faceheart/i18n";
import { ensureChartsRegistered, type MetricMeta } from "./chartSetup";
import type { EmployeeBaseline, MeasurementSeriesResponse } from "@faceheart/api-types";

ensureChartsRegistered();

interface Props {
  meta: MetricMeta;
  series: MeasurementSeriesResponse["series"][number] | undefined;
  baseline?: EmployeeBaseline;
  /** Chart body height (Tailwind class). Larger in the zoom modal. */
  height?: string;
}

export function TrendChart({ meta, series, baseline, height = "h-40" }: Props) {
  const { t } = useI18n("admin");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    /* no-op effect kept for future ResizeObserver tweaks */
  }, []);

  const label = t(`trends.metrics.${meta.key}`);
  const points = series?.points ?? [];

  const datasets: ChartDataset<"line">[] = [
    {
      label,
      data: points.map((p) => ({ x: new Date(p.t).getTime(), y: p.v })),
      borderColor: meta.color,
      backgroundColor: meta.color + "33",
      tension: 0.25,
      pointRadius: 2,
      borderWidth: 1.5,
      fill: false,
    },
  ];

  // Optional baseline band — only for the 5 metrics that ship a personal
  // baseline and only after warmup (the server only returns rows with
  // n>=7, so presence implies validity).
  if (meta.hasBaseline && baseline) {
    const min = baseline.mean - baseline.sd;
    const max = baseline.mean + baseline.sd;
    const xs = points.map((p) => new Date(p.t).getTime());
    if (xs.length > 0) {
      datasets.push(
        {
          label: t("trends.baselinePlus"),
          data: xs.map((x) => ({ x, y: max })),
          borderColor: "rgba(255, 170, 0, 0.25)",
          borderWidth: 1,
          pointRadius: 0,
          fill: "+1",
          backgroundColor: "rgba(255, 170, 0, 0.08)",
          tension: 0,
        },
        {
          label: t("trends.baselineMinus"),
          data: xs.map((x) => ({ x, y: min })),
          borderColor: "rgba(255, 170, 0, 0.25)",
          borderWidth: 1,
          pointRadius: 0,
          fill: false,
          tension: 0,
        },
      );
    }
  }

  const opts: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#091f38",
        titleColor: "#e4f2ff",
        bodyColor: "#7ab3d0",
        borderColor: "#1a5580",
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        type: "time",
        time: { tooltipFormat: "yyyy-MM-dd HH:mm" },
        grid: { color: "rgba(13, 51, 85, 0.4)" },
        ticks: { color: "#3d6b8a", maxRotation: 0, autoSkipPadding: 12 },
      },
      y: {
        grid: { color: "rgba(13, 51, 85, 0.4)" },
        ticks: { color: "#7ab3d0" },
        // Pad the SDK range slightly so the line never hugs the edge.
        suggestedMin: meta.sdkMin - (meta.sdkMax - meta.sdkMin) * 0.05,
        suggestedMax: meta.sdkMax + (meta.sdkMax - meta.sdkMin) * 0.05,
      },
    },
  };

  return (
    <div className="bg-admin-bg2 border border-admin-border rounded-lg p-4 flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm text-admin-text2">{label}</h3>
        <div className="text-xs text-admin-text3">
          {t("trends.rangeLabel", {
            min: meta.sdkMin,
            max: meta.sdkMax,
            unit: meta.unit,
          })}
          {baseline && (
            <span className="ml-3 text-admin-orange">
              {t("trends.baselineLabel", {
                mean: baseline.mean.toFixed(1),
                sd: baseline.sd.toFixed(2),
              })}
            </span>
          )}
        </div>
      </div>
      <div ref={ref} className={height}>
        {points.length === 0 ? (
          <div className="h-full flex items-center justify-center text-admin-text3 text-xs">
            {t("trends.noData", { label })}
          </div>
        ) : (
          <Line data={{ datasets }} options={opts} />
        )}
      </div>
    </div>
  );
}
